function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// Wrapper for the Console for use in a golden layout container
// Will probably need to handle window popping out from golden layout here.
import React, { PureComponent } from 'react';
import { nanoid } from 'nanoid';
import debounce from 'lodash.debounce';
import { connect } from 'react-redux';
import { LoadingOverlay } from '@deephaven/components';
import { Console, ConsoleConstants, HeapUsage, ObjectIcon } from '@deephaven/console';
import { emitCloseDashboard, emitCreateDashboard, emitPanelOpen, emitPanelClose, LayoutManagerContext, LayoutUtils, PanelEvent } from '@deephaven/dashboard';
import { getVariableDescriptor } from '@deephaven/jsapi-bootstrap';
import Log from '@deephaven/log';
import { getCommandHistoryStorage, getPlugins, getTimeZone } from '@deephaven/redux';
import { assertNotNull } from '@deephaven/utils';
import { getIconForPlugin, getWidgetDashboardPlugin, pluginSupportsType } from '@deephaven/plugin';
import { ConsoleEvent } from "../events/index.js";
import Panel from "./CorePanel.js";
import { getDashboardSessionWrapper } from "../redux/index.js";
import "./ConsolePanel.css";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var log = Log.module('ConsolePanel');
var DEBOUNCE_PANEL_STATE_UPDATE = 500;
var DEFAULT_PANEL_STATE = Object.freeze({
  consoleSettings: {},
  itemIds: []
});
export class ConsolePanel extends PureComponent {
  // eslint-disable-next-line react/static-property-placement, react/sort-comp

  constructor(props) {
    super(props);
    _defineProperty(this, "consoleRef", void 0);
    _defineProperty(this, "objectSubscriptionCleanup", void 0);
    _defineProperty(this, "savePanelState", debounce(() => {
      var _this$state = this.state,
        consoleSettings = _this$state.consoleSettings,
        itemIds = _this$state.itemIds;
      var panelState = {
        consoleSettings,
        itemIds: [...itemIds]
      };
      log.debug('Saving panel state', panelState);
      // eslint-disable-next-line react/no-unused-state
      this.setState({
        panelState
      });
    }, DEBOUNCE_PANEL_STATE_UPDATE));
    this.handleFocusCommandHistory = this.handleFocusCommandHistory.bind(this);
    this.handleOpenObject = this.handleOpenObject.bind(this);
    this.handleCloseObject = this.handleCloseObject.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleSettingsChange = this.handleSettingsChange.bind(this);
    this.handleShow = this.handleShow.bind(this);
    this.handlePanelMount = this.handlePanelMount.bind(this);
    this.supportsType = this.supportsType.bind(this);
    this.iconForType = this.iconForType.bind(this);
    this.consoleRef = /*#__PURE__*/React.createRef();
    var initialPanelState = props.panelState;
    var _panelState = _objectSpread(_objectSpread({}, DEFAULT_PANEL_STATE), initialPanelState || {});
    var _consoleSettings = _panelState.consoleSettings,
      _itemIds = _panelState.itemIds;
    this.state = {
      consoleSettings: _consoleSettings,
      itemIds: new Map(_itemIds),
      objectMap: new Map(),
      error: undefined,
      // eslint-disable-next-line react/no-unused-state
      panelState: _panelState // Dehydrated panel state that can load this panel
    };
  }
  componentDidMount() {
    var glEventHub = this.props.glEventHub;
    // Need to close the disconnected panels when we're first loaded,
    // as they may have been saved with the dashboard
    this.closeDisconnectedPanels();
    glEventHub.on(PanelEvent.MOUNT, this.handlePanelMount);
    this.subscribeToFieldUpdates();
  }
  componentDidUpdate(prevProps, prevState) {
    var _this$state2 = this.state,
      consoleSettings = _this$state2.consoleSettings,
      itemIds = _this$state2.itemIds;
    if (prevState.consoleSettings !== consoleSettings || prevState.itemIds !== itemIds) {
      this.savePanelState();
    }
  }
  componentWillUnmount() {
    var _this$objectSubscript;
    var glEventHub = this.props.glEventHub;
    this.savePanelState.flush();
    glEventHub.off(PanelEvent.MOUNT, this.handlePanelMount);
    (_this$objectSubscript = this.objectSubscriptionCleanup) === null || _this$objectSubscript === void 0 || _this$objectSubscript.call(this);
  }
  subscribeToFieldUpdates() {
    var sessionWrapper = this.props.sessionWrapper;
    if (sessionWrapper == null) {
      return;
    }
    var session = sessionWrapper.session;
    this.objectSubscriptionCleanup = session.subscribeToFieldUpdates(updates => {
      log.debug('Got updates', updates);
      this.setState(_ref => {
        var objectMap = _ref.objectMap;
        var updated = updates.updated,
          created = updates.created,
          removed = updates.removed;

        // Remove from the array if it's been removed OR modified. We'll add it back after if it was modified.
        var objectsToRemove = [...updated, ...removed];
        var newObjectMap = new Map(objectMap);
        objectsToRemove.forEach(toRemove => {
          var title = toRemove.title;
          if (title !== undefined) {
            newObjectMap.delete(title);
          }
        });

        // Now add all the modified and updated widgets back in
        var objectsToAdd = [...updated, ...created];
        objectsToAdd.forEach(toAdd => {
          if (toAdd.title !== undefined) {
            newObjectMap.set(toAdd.title, toAdd);
          }
        });
        return {
          objectMap: newObjectMap
        };
      });
    });
  }
  setItemId(name, id) {
    this.setState(_ref2 => {
      var itemIds = _ref2.itemIds;
      var newItemIds = new Map(itemIds);
      newItemIds.set(name, id);
      return {
        itemIds: newItemIds
      };
    });
  }
  getItemId(name) {
    var createIfNecessary = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var itemIds = this.state.itemIds;
    var id = itemIds.get(name);
    if (id == null && createIfNecessary) {
      id = nanoid();
      this.setItemId(name, id);
    }
    return id;
  }

  /**
   * Removes the item ID mapping from state by its ID value.
   * @param id The item ID to delete from the itemIds map.
   */
  deleteItemId(id) {
    this.setState(_ref3 => {
      var itemIds = _ref3.itemIds;
      log.debug('Delete item', id, itemIds);
      var key = [...itemIds.keys()].find(k => itemIds.get(k) === id);
      if (key === undefined) {
        return null;
      }
      var newItemIds = new Map(itemIds);
      newItemIds.delete(key);
      return {
        itemIds: newItemIds
      };
    });
  }
  handleTabFocus() {
    var _this$consoleRef$curr;
    (_this$consoleRef$curr = this.consoleRef.current) === null || _this$consoleRef$curr === void 0 || _this$consoleRef$curr.focus();
  }
  handlePanelMount(panel) {
    var _panel$props, _panel$props2;
    var itemIds = this.state.itemIds;
    var panelId = panel === null || panel === void 0 || (_panel$props = panel.props) === null || _panel$props === void 0 ? void 0 : _panel$props.id;
    var sourceId = panel === null || panel === void 0 || (_panel$props2 = panel.props) === null || _panel$props2 === void 0 || (_panel$props2 = _panel$props2.metadata) === null || _panel$props2 === void 0 ? void 0 : _panel$props2.sourcePanelId;
    if (panelId && sourceId) {
      // Check if the panel was created from a panel in this console
      var pandelIds = new Set(itemIds.values());
      if (pandelIds.has(sourceId)) {
        // The Chart Panel does not have name so map panelId to panelId
        this.setItemId(panelId, panelId);
      }
    }
  }

  /**
   * Handles a panel closed event. Removes the panel's item ID mapping unless
   * the panel was replaced (i.e. a panel with the same ID still exists in the layout),
   * in which case the mapping is retained for the replacement panel.
   * @param panelId The ID of the panel that was closed.
   */
  handlePanelClosed(panelId) {
    // When replacing a panel, LayoutUtils.openComponent adds the new panel before removing the old
    // one, so the old panel's CLOSED event fires with the same panelId as the new panel.
    // If a panel with panelId still exists, it was replaced and we should not delete the itemId.
    var glContainer = this.props.glContainer;
    var root = LayoutUtils.getRootFromContainer(glContainer);
    var stack = LayoutUtils.getStackForConfig(root, {
      id: panelId
    });
    if (stack !== null && LayoutUtils.getContentItemInStack(stack, {
      id: panelId
    }) !== null) {
      return;
    }
    this.deleteItemId(panelId);
  }
  handleFocusCommandHistory() {
    var glEventHub = this.props.glEventHub;
    glEventHub.emit(ConsoleEvent.FOCUS_HISTORY);
  }
  handleResize() {
    this.updateDimensions();
  }
  handleShow() {
    this.updateDimensions();
  }
  handleOpenObject(object) {
    var forceOpen = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    assertNotNull(this.context);
    var root = this.context.root;
    var oldPanelId = object.title != null ? this.getItemId(object.title, false) : null;
    if (forceOpen || oldPanelId != null && LayoutUtils.getStackForRoot(root, {
      id: oldPanelId
    }, false, false, false) != null) {
      this.openWidget(object);
    }
  }
  handleCloseObject(object) {
    var title = object.title;
    if (title !== undefined) {
      var id = this.getItemId(title, false);
      if (id != null) {
        var glEventHub = this.props.glEventHub;
        emitPanelClose(glEventHub, id);
        // Just emit for all panels since there shouldn't be dashboard and panel name conflicts
        emitCloseDashboard(glEventHub, title);
      }
    }
  }
  handleSettingsChange(consoleSettings) {
    var glEventHub = this.props.glEventHub;
    log.debug('handleSettingsChange', consoleSettings);
    this.setState({
      consoleSettings
    });
    glEventHub.emit(ConsoleEvent.SETTINGS_CHANGED, consoleSettings);
  }

  /**
   * @param widget The widget to open
   */
  openWidget(widget) {
    var _this$props = this.props,
      glEventHub = _this$props.glEventHub,
      plugins = _this$props.plugins;
    var type = widget.type;
    var widgetDashboardPlugin = getWidgetDashboardPlugin(plugins, type);
    if (widgetDashboardPlugin != null) {
      // If it can be opened as a dashboard, open it as a dashboard. Otherwise, open it as a panel.
      var dashboardPayload = widgetDashboardPlugin.createDashboardPayload(widget);
      log.debug('openDashboardWidget', dashboardPayload);
      emitCreateDashboard(glEventHub, dashboardPayload);
    } else {
      this.openPanelWidget(widget);
    }
  }

  /**
   * Open the given widget in a new panel. This is used for all widget types except "widget dashboard" types, which are widgets that should be opened in a new dashboard instead of a panel.
   * @param widget The widget to open
   */
  openPanelWidget(widget) {
    var _this$props2 = this.props,
      glEventHub = _this$props2.glEventHub,
      sessionWrapper = _this$props2.sessionWrapper;
    assertNotNull(sessionWrapper);
    var config = sessionWrapper.config,
      session = sessionWrapper.session;
    var _widget$title = widget.title,
      title = _widget$title === void 0 ? widget.name : _widget$title;
    assertNotNull(title);
    var itemId = this.getItemId(title);
    assertNotNull(itemId);
    var openOptions = {
      fetch: () => session.getObject(widget),
      panelId: itemId,
      widget: _objectSpread(_objectSpread({}, getVariableDescriptor(widget)), {}, {
        sessionId: config.id
      })
    };
    log.debug('openPanelWidget', openOptions);
    emitPanelOpen(glEventHub, openOptions);
  }
  addCommand(command) {
    var _this$consoleRef$curr2;
    var focus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var execute = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    (_this$consoleRef$curr2 = this.consoleRef.current) === null || _this$consoleRef$curr2 === void 0 || _this$consoleRef$curr2.addCommand(command, focus, execute);
  }

  /**
   * Close the disconnected panels from this session
   * @param force True to force the panels closed regardless of the current setting
   */
  closeDisconnectedPanels() {
    var force = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    var _this$state3 = this.state,
      consoleSettings = _this$state3.consoleSettings,
      itemIds = _this$state3.itemIds;
    var _consoleSettings$isCl = consoleSettings.isClosePanelsOnDisconnectEnabled,
      isClosePanelsOnDisconnectEnabled = _consoleSettings$isCl === void 0 ? true : _consoleSettings$isCl;
    if (!isClosePanelsOnDisconnectEnabled && !force) {
      return;
    }
    var panelIdsToClose = [...itemIds.values()];
    var glEventHub = this.props.glEventHub;
    panelIdsToClose.forEach(panelId => {
      emitPanelClose(glEventHub, panelId);
    });
    this.setState({
      itemIds: new Map()
    });
  }
  updateDimensions() {
    var _this$consoleRef$curr3;
    (_this$consoleRef$curr3 = this.consoleRef.current) === null || _this$consoleRef$curr3 === void 0 || _this$consoleRef$curr3.updateDimensions();
  }
  supportsType(type) {
    var plugins = this.props.plugins;
    return [...plugins.values()].some(plugin => pluginSupportsType(plugin, type));
  }
  iconForType(type) {
    var plugins = this.props.plugins;
    var plugin = [...plugins.values()].find(p => pluginSupportsType(p, type));
    if (plugin != null) {
      return getIconForPlugin(plugin);
    }
    // TODO: #1573 Remove this default and always return getIconForPlugin
    return /*#__PURE__*/_jsx(ObjectIcon, {
      type: type
    });
  }
  render() {
    var _this$props3 = this.props,
      commandHistoryStorage = _this$props3.commandHistoryStorage,
      glContainer = _this$props3.glContainer,
      glEventHub = _this$props3.glEventHub,
      sessionWrapper = _this$props3.sessionWrapper,
      timeZone = _this$props3.timeZone,
      unzip = _this$props3.unzip;
    if (sessionWrapper == null) {
      return /*#__PURE__*/_jsx(LoadingOverlay, {
        isLoading: false,
        errorMessage: "Console is disabled.",
        "data-testid": "console-panel-loading-error"
      });
    }
    var _this$state4 = this.state,
      consoleSettings = _this$state4.consoleSettings,
      error = _this$state4.error,
      objectMap = _this$state4.objectMap;
    var config = sessionWrapper.config,
      session = sessionWrapper.session,
      connection = sessionWrapper.connection,
      _sessionWrapper$detai = sessionWrapper.details,
      details = _sessionWrapper$detai === void 0 ? {} : _sessionWrapper$detai,
      dh = sessionWrapper.dh;
    var workerName = details.workerName,
      processInfoId = details.processInfoId;
    var sessionId = config.id,
      language = config.type;
    return /*#__PURE__*/_jsx(Panel, {
      className: "iris-panel-console",
      componentPanel: this,
      glContainer: glContainer,
      glEventHub: glEventHub,
      onResize: this.handleResize,
      onShow: this.handleShow,
      onTabFocus: this.handleTabFocus,
      errorMessage: error != null ? "".concat(error) : undefined,
      children: session != null && /*#__PURE__*/_jsx(Console, {
        dh: dh,
        ref: this.consoleRef,
        settings: consoleSettings,
        session: session,
        focusCommandHistory: this.handleFocusCommandHistory,
        openObject: this.handleOpenObject,
        closeObject: this.handleCloseObject,
        commandHistoryStorage: commandHistoryStorage,
        onSettingsChange: this.handleSettingsChange,
        language: language,
        statusBarChildren: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("div", {
            children: ConsoleConstants.LANGUAGE_MAP.get(language)
          }), workerName != null && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx("div", {
              children: "\u2022"
            }), workerName]
          }), processInfoId != null && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx("div", {
              children: "\u2022"
            }), processInfoId, /*#__PURE__*/_jsx("div", {
              children: "\u2022"
            })]
          }), /*#__PURE__*/_jsx(HeapUsage, {
            connection: connection,
            defaultUpdateInterval: 10 * 1000,
            hoverUpdateInterval: 3 * 1000,
            monitorDuration: 10 * 60 * 1000
          })]
        }),
        showObjectsMenu: false,
        scope: sessionId,
        timeZone: timeZone,
        objectMap: objectMap,
        unzip: unzip,
        supportsType: this.supportsType,
        iconForType: this.iconForType
      })
    });
  }
}
_defineProperty(ConsolePanel, "COMPONENT", 'ConsolePanel');
_defineProperty(ConsolePanel, "TITLE", 'Console');
_defineProperty(ConsolePanel, "contextType", LayoutManagerContext);
var mapStateToProps = (state, ownProps) => ({
  commandHistoryStorage: getCommandHistoryStorage(state),
  sessionWrapper: getDashboardSessionWrapper(state, ownProps.localDashboardId),
  timeZone: getTimeZone(state),
  plugins: getPlugins(state)
});
var ConnectedConsolePanel = connect(mapStateToProps, null, null, {
  forwardRef: true
})(ConsolePanel);
export default ConnectedConsolePanel;
//# sourceMappingURL=ConsolePanel.js.map